import React, { useState, useRef } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { useApp } from '../../context/AppContext';
import { toast } from 'sonner';
import { X, ShieldCheck, ArrowRight, ArrowLeft, UploadCloud, Clock, CheckCircle2, FileText } from 'lucide-react';

const STEPS = ['PERSONAL INFO', 'IDENTITY DOCUMENT', 'SEC ACCREDITATION'];

const ID_TYPES = ['PASSPORT', "DRIVER'S LICENSE", 'NATIONAL ID / AADHAAR'];
const ACCREDITATION = [
  { id: 'networth', title: 'Net Worth > $1,000,000 USD', desc: 'Individual or joint net worth exceeding $1M (excluding primary residence).' },
  { id: 'income', title: 'Annual Income > $200,000 USD ($300K Joint)', desc: 'Income exceeding threshold in each of the two most recent tax years.' },
  { id: 'finra', title: 'FINRA License Holder (Series 7, 65, or 82)', desc: 'Registered professional representative in good standing.' },
  { id: 'institution', title: 'Institutional / Entity with $5M+ Assets', desc: 'Corporation, LLC, Family Office, or Trust with over $5M in total assets.' },
];

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)] block mb-1.5">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-[14px] text-white placeholder:text-[var(--muted-2)] focus:outline-none focus:border-[var(--orange)] transition-colors";

function FileDrop({ label, file, onFile }) {
  const ref = useRef();
  return (
    <div
      onClick={() => ref.current?.click()}
      className="border border-dashed border-[var(--border)] rounded-xl py-7 px-4 text-center cursor-pointer hover:border-[var(--orange)] transition-colors"
    >
      <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => onFile(e.target.files?.[0] || null)} />
      {file ? (
        <div className="flex items-center justify-center gap-2 text-[var(--green)]">
          <FileText size={18} /> <span className="text-[13px]">{file.name}</span>
        </div>
      ) : (
        <>
          <UploadCloud size={26} className="mx-auto text-[var(--orange)]" />
          <p className="text-[13px] text-white mt-2 font-medium">{label}</p>
          <p className="text-[11px] text-[var(--muted-2)] mt-1">Supported: PDF, PNG, JPG (max 25MB)</p>
        </>
      )}
    </div>
  );
}

export default function KycModal({ open, onClose }) {
  const { kycStatus, submitKyc } = useApp();
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    fullName: '', dob: '', phone: '', country: 'United States', address: '', ssnLast4: '',
    idType: 'PASSPORT', idNumber: '', accreditation: 'networth', estimatedNetWorth: '', declaration: false,
  });
  const [idDoc, setIdDoc] = useState(null);
  const [proofDoc, setProofDoc] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const close = () => {
    setStep(0); setSubmitted(false);
    onClose();
  };

  const submit = async () => {
    if (!f.declaration) { toast.error('Please accept the declaration'); return; }
    setBusy(true);
    const res = await submitKyc({
      ...f,
      idDocName: idDoc?.name || '',
      proofDocName: proofDoc?.name || '',
    });
    setBusy(false);
    if (res.ok) setSubmitted(true);
    else toast.error('Submission failed, please try again');
  };

  const alreadyDone = kycStatus === 'verified' || kycStatus === 'pending';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[var(--panel)] border-[var(--border)] text-[var(--text)] max-w-3xl p-0 gap-0 [&>button]:hidden max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[var(--border)]">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#1a1030] border border-[#3f2a5a] flex items-center justify-center">
              <ShieldCheck size={20} className="text-[#c084fc]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-display font-bold text-[18px] text-white">INSTITUTIONAL KYC & ACCREDITATION DESK</h2>
              </div>
              <span className="inline-block mt-1 text-[9px] font-mono px-2 py-0.5 rounded bg-[#2a1a3a] text-[#c084fc] border border-[#3f2a5a]">SEC RULE 506(c)</span>
              <p className="text-[10px] font-mono tracking-[0.1em] text-[var(--muted-2)] mt-2">VERIFY YOUR LEGAL IDENTITY AND UPLOAD DOCUMENTS FOR SECONDARY TRADING CLEARANCE</p>
            </div>
          </div>
          <button onClick={close} className="text-[var(--muted)] hover:text-white p-1"><X size={18} /></button>
        </div>

        {submitted ? (
          <div className="p-10 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-[#0e2a20] border border-[#1e5b45] flex items-center justify-center">
              <Clock size={30} className="text-[var(--green)]" />
            </div>
            <h3 className="font-display font-bold text-[22px] text-white mt-5">KYC Submitted Successfully</h3>
            <p className="text-[14px] text-[var(--muted)] mt-3 max-w-md leading-relaxed">
              Your accreditation documents have been sent to our compliance desk for SEC Rule 506(c) review.
              <span className="text-white"> Within 24 hours</span> you will be notified whether your KYC has been
              <span className="text-[var(--green)]"> approved</span> or <span className="text-[var(--red)]">rejected</span>.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono px-3 py-1.5 rounded-full bg-[#2a1a0e] text-[var(--orange)] border border-[#4a2f14]">
              <Clock size={13} /> STATUS: UNDER REVIEW (PENDING)
            </div>
            <button onClick={close} className="mt-7 px-8 py-3 rounded-lg bg-[var(--orange)] text-white font-semibold text-[14px] hover:bg-[var(--orange-bright)] transition-colors">Done</button>
          </div>
        ) : alreadyDone ? (
          <div className="p-10 flex flex-col items-center text-center">
            {kycStatus === 'verified' ? (
              <>
                <CheckCircle2 size={44} className="text-[var(--green)]" />
                <h3 className="font-display font-bold text-[20px] text-white mt-4">Accreditation Verified</h3>
                <p className="text-[13px] text-[var(--muted)] mt-2 max-w-md">Your accredited investor status is active. You may transact across all unlisted markets.</p>
              </>
            ) : (
              <>
                <Clock size={40} className="text-[var(--orange)]" />
                <h3 className="font-display font-bold text-[20px] text-white mt-4">KYC Under Review</h3>
                <p className="text-[13px] text-[var(--muted)] mt-2 max-w-md">Your submission is being reviewed by our compliance desk. You'll be notified within 24 hours whether it is approved or rejected.</p>
              </>
            )}
            <button onClick={close} className="mt-7 px-8 py-3 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] text-white text-[13px] hover:border-[#3a3a42] transition-colors">Close</button>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex items-center gap-6 px-6 pt-4 border-b border-[var(--border)]">
              {STEPS.map((s, i) => (
                <button key={s} onClick={() => i < step && setStep(i)} className={`pb-3 text-[11.5px] font-mono tracking-wide border-b-2 transition-colors ${i === step ? 'text-[#c084fc] border-[#c084fc]' : 'text-[var(--muted-2)] border-transparent'}`}>
                  {i + 1}. {s}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* STEP 1 */}
              {step === 0 && (
                <div className="space-y-5">
                  <div className="p-3.5 rounded-lg bg-[#1a1030] border border-[#3f2a5a] text-[12.5px] text-[#d8c4f0]">
                    US federal securities laws (SEC Rule 506(c)) mandate complete identity and background verification before secondary private stock orders can be settled.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="FULL LEGAL NAME"><input className={inputCls} placeholder="e.g. Om Santiom" value={f.fullName} onChange={(e) => set('fullName', e.target.value)} /></Field>
                    <Field label="DATE OF BIRTH"><input className={inputCls} type="date" value={f.dob} onChange={(e) => set('dob', e.target.value)} /></Field>
                    <Field label="PHONE NUMBER (SMS VERIFICATION)"><input className={inputCls} placeholder="+1 (555) 234-5678" value={f.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
                    <Field label="COUNTRY OF CITIZENSHIP / TAX RESIDENCE"><input className={inputCls} value={f.country} onChange={(e) => set('country', e.target.value)} /></Field>
                    <Field label="RESIDENTIAL STREET ADDRESS"><input className={inputCls} placeholder="Street, City, State, ZIP" value={f.address} onChange={(e) => set('address', e.target.value)} /></Field>
                    <Field label="SSN / TIN (LAST 4)"><input className={inputCls} maxLength={4} placeholder="8832" value={f.ssnLast4} onChange={(e) => set('ssnLast4', e.target.value.replace(/[^0-9]/g, ''))} /></Field>
                  </div>
                  <button onClick={() => setStep(1)} className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-[#8b5cf6] text-white font-semibold text-[14px] hover:bg-[#7c4ff0] transition-colors">
                    PROCEED TO IDENTITY DOCUMENT <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {/* STEP 2 */}
              {step === 1 && (
                <div className="space-y-5">
                  <Field label="SELECT GOVERNMENT ID TYPE">
                    <div className="grid grid-cols-3 gap-3">
                      {ID_TYPES.map((t) => (
                        <button key={t} onClick={() => set('idType', t)} className={`py-3 rounded-lg text-[12px] font-medium border transition-colors ${f.idType === t ? 'border-[#8b5cf6] bg-[#1a1030] text-white' : 'border-[var(--border)] text-[var(--muted)] hover:text-white'}`}>{t}</button>
                      ))}
                    </div>
                  </Field>
                  <Field label="OFFICIAL DOCUMENT ID / PASSPORT NUMBER"><input className={inputCls} placeholder="P98421894" value={f.idNumber} onChange={(e) => set('idNumber', e.target.value)} /></Field>
                  <Field label="UPLOAD GOVERNMENT ID DOCUMENT (PHOTO / PDF SCAN)">
                    <FileDrop label="DRAG & DROP GOVERNMENT ID SCAN OR CLICK TO BROWSE" file={idDoc} onFile={setIdDoc} />
                  </Field>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setStep(0)} className="flex items-center gap-2 px-5 py-3 rounded-lg border border-[var(--border)] text-[var(--muted)] text-[13px] hover:text-white transition-colors"><ArrowLeft size={15} /> BACK</button>
                    <button onClick={() => setStep(2)} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg bg-[#8b5cf6] text-white font-semibold text-[14px] hover:bg-[#7c4ff0] transition-colors">PROCEED TO ACCREDITATION <ArrowRight size={16} /></button>
                  </div>
                </div>
              )}

              {/* STEP 3 */}
              {step === 2 && (
                <div className="space-y-4">
                  <div className="p-3.5 rounded-lg bg-[#2a230e] border border-[#4a3f14] text-[12.5px] text-[#e8d99a]">
                    <b className="text-[var(--orange)]">SEC Rule 506(c) Accreditation:</b> Select your qualifying accreditation framework and upload verification documentation (W-2, tax return, CPA certification, or brokerage net worth statement).
                  </div>
                  <div className="space-y-2.5">
                    {ACCREDITATION.map((a) => (
                      <label key={a.id} className={`flex items-start gap-3 p-3.5 rounded-lg border cursor-pointer transition-colors ${f.accreditation === a.id ? 'border-[#8b5cf6] bg-[#1a1030]' : 'border-[var(--border)] hover:border-[#3a3a42]'}`}>
                        <input type="radio" name="acc" checked={f.accreditation === a.id} onChange={() => set('accreditation', a.id)} className="mt-0.5 accent-[#8b5cf6]" />
                        <div>
                          <div className="text-[13.5px] text-white font-medium">{a.title}</div>
                          <div className="text-[11.5px] text-[var(--muted)] mt-0.5">{a.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <Field label="ESTIMATED NET WORTH ($USD)"><input className={inputCls} placeholder="2500000" value={f.estimatedNetWorth} onChange={(e) => set('estimatedNetWorth', e.target.value.replace(/[^0-9]/g, ''))} /></Field>
                  <Field label="ACCREDITATION PROOF DOCUMENT (CPA LETTER, TAX RETURN, OR BROKERAGE LETTER)">
                    <FileDrop label="UPLOAD ACCREDITATION PROOF DOCUMENT" file={proofDoc} onFile={setProofDoc} />
                  </Field>
                  <label className="flex items-start gap-3 p-3 cursor-pointer">
                    <input type="checkbox" checked={f.declaration} onChange={(e) => set('declaration', e.target.checked)} className="mt-0.5 accent-[#8b5cf6]" />
                    <span className="text-[12px] text-[var(--muted)]">I declare under penalty of perjury that the submitted information and financial documents are authentic and meet the requirements of SEC Rule 506(c).</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setStep(1)} className="flex items-center gap-2 px-5 py-3 rounded-lg border border-[var(--border)] text-[var(--muted)] text-[13px] hover:text-white transition-colors"><ArrowLeft size={15} /> BACK</button>
                    <button onClick={submit} disabled={busy} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg bg-[#8b5cf6] text-white font-semibold text-[14px] hover:bg-[#7c4ff0] transition-colors disabled:opacity-60">
                      <ShieldCheck size={16} /> {busy ? 'SUBMITTING…' : 'SUBMIT KYC FOR SEC CLEARANCE'}
                    </button>
                  </div>
                  <p className="text-center text-[10px] font-mono text-[var(--muted-2)]">DEMO ONLY · DO NOT ENTER REAL PERSONAL DATA</p>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
